/**
 * Widget Styling Utils - Automatische Farb- und Font-Anwendung
 * 
 * Wendet Space-Settings automatisch auf manuell hinzugefügte Widgets an
 */

// Import der AI Design System Logik
import { getDatabase, ref, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';

/**
 * Lädt Page Settings (mit Color Assignments) für Widget-Styling
 */
const loadPageSettings = async (userId, spaceId, pageName) => {
    if (!userId || !spaceId || !pageName) return null;
    
    try {
        const db = getDatabase();
        const pageRef = ref(db, `/pages/${userId}/${spaceId}/${pageName}`);
        const snapshot = await get(pageRef);
        
        if (snapshot.exists()) {
            const pageData = snapshot.val();
            console.log(`🎨 [PAGE-SETTINGS] Loaded page settings for ${pageName}`);
            
            // 🛡️ CHECK: Hat diese Page eigene Color Assignments?
            const hasOwnColorAssignments = pageData.colorAssignments && 
                (pageData.generatedBy === 'AI' || pageData.hasCustomColorAssignments === true);
            
            if (hasOwnColorAssignments) {
                console.log(`🛡️ [PAGE-ISOLATION] Page ${pageName} has isolated color assignments`);
                console.log(`🛡️ [PAGE-ISOLATION] Page assignments:`, pageData.colorAssignments);
                console.log(`🛡️ [PAGE-ISOLATION] Using page-specific colors:`, pageData.colors);
            }
            
            return {
                colors: pageData.colors,
                fonts: {
                    globalBigFont: pageData.globalBigFont || 'Voyage',
                    globalSmallFont: pageData.globalSmallFont || 'Jost'
                },
                // PRIORITÄT: Page Color Assignments (wenn vorhanden) > Space Default Assignments
                colorAssignments: pageData.colorAssignments || {
                    background: 'color5',
                    primaryText: 'color2',
                    secondaryText: 'color3',
                    buttons: 'color1',
                    buttonText: 'color5',
                    accents: 'color1'
                },
                // Metadata für Debugging
                hasOwnColorAssignments: hasOwnColorAssignments,
                generatedBy: pageData.generatedBy,
                pageName: pageName
            };
        }
    } catch (error) {
        console.error('❌ Error loading page settings for widget styling:', error);
    }
    
    return null;
};

/**
 * Fallback: Lädt Space Settings falls Page Settings nicht verfügbar
 */
const loadSpaceSettings = async (userId, spaceId) => {
    if (!userId || !spaceId) return null;
    
    try {
        const db = getDatabase();
        const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
        const snapshot = await get(spaceRef);
        
        if (snapshot.exists()) {
            const spaceData = snapshot.val();
            console.log(`🎨 [SPACE-SETTINGS] Loaded space settings as fallback`);
            
            return {
                colors: spaceData.designSettings?.colors || spaceData.colors,
                fonts: {
                    globalBigFont: spaceData.designSettings?.globalBigFont || spaceData.globalBigFont || 'Voyage',
                    globalSmallFont: spaceData.designSettings?.globalSmallFont || spaceData.globalSmallFont || 'Jost'
                },
                colorAssignments: spaceData.designSettings?.colorAssignments || {
                    background: 'color5',
                    primaryText: 'color2',
                    secondaryText: 'color3',
                    buttons: 'color1',
                    buttonText: 'color5',
                    accents: 'color1'
                }
            };
        }
    } catch (error) {
        console.error('❌ Error loading space settings for widget styling:', error);
    }
    
    return null;
};

/**
 * Widget-spezifische Design-Konfigurationen (vereinfacht aus aiDesignSystem.js)
 */
const WIDGET_STYLING_CONFIGS = {
    AchievementsBanner: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            numberColor: 'primaryText',
            textColor: 'secondaryText',
            lineColor: 'accents',
            iconColor: 'buttons'
        },
        fontFields: {
            titleFont: 'big',
            numberFont: 'big',
            textFont: 'small'
        }
    },
    
    SpiritualQuoteCard: {
        colorFields: {
            backgroundColor: 'background',
            containerBackground: 'background', // ✅ Container sollte GLEICHE Farbe wie Background haben
            titleColor: 'primaryText',
            richTextColor: 'secondaryText',
            topIconColor: 'buttons',
            decorativeSymbolColor: 'accents'
        },
        fontFields: {
            titleFont: 'big',
            richTextFont: 'small'
        }
    },
    
    HeroHeader: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            subtitleColor: 'secondaryText',
            descriptionColor: 'secondaryText',
            additionalColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            subtitleFont: 'small',
            descriptionFont: 'small',
            additionalFont: 'small'
        }
    },
    
    PersonalBrandingWidget: {
        colorFields: {
            backgroundColor: 'background',
            overlayNameColor: 'primaryText',
            overlayTitleColor: 'accents',
            mainQuestionColor: 'primaryText',
            helpTextColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            bioFont: 'small'
        }
    },
    
    HiWidget: {
        colorFields: {
            backgroundColor: 'background',
            greetingColor: 'primaryText',
            titleColor: 'primaryText',
            descriptionColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            greetingFont: 'big',
            titleFont: 'big',
            descriptionFont: 'small'
        }
    },
    
    VideoTeaser: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            descriptionColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            descriptionFont: 'small'
        }
    },
    
    SimplisticHeader: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            subtitleColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            subtitleFont: 'small'
        }
    },
    
    SupremeTestimonial: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            testimonialTextColor: 'secondaryText',
            authorNameColor: 'primaryText'
        },
        fontFields: {
            titleFont: 'big',
            testimonialTextFont: 'small',
            authorNameFont: 'small'
        }
    },
    
    SimplisticQuote: {
        colorFields: {
            backgroundColor: 'background',
            simpleTextColor: 'secondaryText',
            richTextColor: 'primaryText'
        },
        fontFields: {
            simpleTextFont: 'small',
            richTextFont: 'big'
        }
    },
    
    MethodologyWidget: {
        colorFields: {
            backgroundColor: 'background',
            mainTitleColor: 'primaryText',
            mainDescriptionColor: 'secondaryText',
            stepTitleColor: 'primaryText',
            stepDescriptionColor: 'secondaryText',
            stepNumberColor: 'primaryText', // ✅ ADDED: Step-Number-Farbe
            additionalTextColor: 'secondaryText', // ✅ ADDED: Zusatztext-Farbe
            conclusionTextColor: 'secondaryText' // ✅ ADDED: Abschlusstext-Farbe
        },
        fontFields: {
            mainTitleFont: 'big',
            mainDescriptionFont: 'small',
            stepTitleFont: 'big',
            stepDescriptionFont: 'small',
            stepNumberFont: 'big', // ✅ ADDED: Step-Number-Font
            additionalTextFont: 'small', // ✅ ADDED: Zusatztext-Font
            conclusionTextFont: 'small' // ✅ ADDED: Abschlusstext-Font
        }
    },
    
    
    SimplisticTestimonial: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            testimonialTextColor: 'secondaryText',
            authorNameColor: 'primaryText'
        },
        fontFields: {
            titleFont: 'big',
            testimonialTextFont: 'small',
            authorNameFont: 'small'
        }
    },
    
    // Neue Widgets
    QAWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            questionColor: 'primaryText',
            answerColor: 'secondaryText'
        },
        fontFields: {
            titleFont: 'big',
            questionFont: 'big',
            answerFont: 'small'
        }
    },
    
    BulletPointsWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            subtitleColor: 'secondaryText',
            bulletTextColor: 'secondaryText',
            bulletPointColor: 'secondaryText', // ✅ ADDED: Alias für bulletPointColor (konsistent mit AI-System)
            bulletIconColor: 'accents',
            bulletIconBackgroundColor: 'background' // ✅ ADDED: Icon-Background-Farbe
        },
        fontFields: {
            titleFont: 'big',
            subtitleFont: 'small',
            bulletTextFont: 'small'
        }
    },
    
    ComparisonWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            beforeTitleColor: 'primaryText',
            afterTitleColor: 'primaryText',
            beforeTextColor: 'secondaryText',
            afterTextColor: 'secondaryText',
            transformationTextColor: 'secondaryText'
        },
        fontFields: {
            titleFont: 'big',
            beforeTitleFont: 'big',
            afterTitleFont: 'big',
            beforeTextFont: 'small',
            afterTextFont: 'small',
            transformationTextFont: 'small'
        }
    },

    // Weitere wichtige Widgets
    InfiniteBanner: {
        colorFields: {
            backgroundColor: 'background',
            textColor: 'primaryText'
        },
        fontFields: {
            fontFamily: 'small'
        }
    },

    BragBar: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText'
        },
        fontFields: {
            titleFont: 'small'
        }
    },

    SpiritualFeatureBanner: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            featureTitleColor: 'primaryText',
            featureDescriptionColor: 'secondaryText'
        },
        fontFields: {
            titleFont: 'big',
            featureTitleFont: 'big',
            featureDescriptionFont: 'small'
        }
    },

    HolisticTestimonial: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            testimonialTextColor: 'secondaryText',
            authorNameColor: 'primaryText'
        },
        fontFields: {
            titleFont: 'big',
            testimonialTextFont: 'small',
            authorNameFont: 'small'
        }
    },

    SpiritualTestimonial: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            testimonialTextColor: 'secondaryText',
            authorNameColor: 'primaryText'
        },
        fontFields: {
            titleFont: 'big',
            testimonialTextFont: 'small',
            authorNameFont: 'small'
        }
    },

    // Product Widgets (grundlegendes Styling)
    ProductInfoWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            descriptionColor: 'secondaryText',
            priceColor: 'accents',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            descriptionFont: 'small'
        }
    },

    EventInfoWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            descriptionColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            descriptionFont: 'small'
        }
    },

    CourseInfoWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            descriptionColor: 'secondaryText',
            buttonColor: 'buttons',
            buttonTextColor: 'buttonText'
        },
        fontFields: {
            titleFont: 'big',
            descriptionFont: 'small'
        }
    },
    
    SimplisticServices: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            subtitleColor: 'secondaryText',
            serviceNameColor: 'primaryText',
            serviceDescriptionColor: 'secondaryText',
            serviceIconColor: 'primaryText',
            serviceIconBackgroundColor: 'background',
            serviceBackgroundColor: 'background',
            serviceBorderColor: 'accents'
        },
        fontFields: {
            titleFont: 'big',
            subtitleFont: 'small',
            serviceNameFont: 'big',
            serviceDescriptionFont: 'small'
        }
    },
    
    ComparisonWidget: {
        colorFields: {
            backgroundColor: 'background',
            titleColor: 'primaryText',
            richTextColor: 'secondaryText',
            imageLabelColor: 'secondaryText',
            imageLabelBackgroundColor: 'background'
        },
        fontFields: {
            titleFont: 'big',
            richTextFont: 'small',
            imageLabelFont: 'small'
        }
    },

    // Footer Widget - WICHTIG für Space Integration
    FancyFooter: {
        colorFields: {
            backgroundColor: 'background',
            textColor: 'secondaryText',        // Haupt-Text-Farbe (statt #ffffff)
            headingColor: 'primaryText',       // Kategorie-Titel-Farbe
            linkColor: 'secondaryText',        // Link-Farbe
            linkHoverColor: 'accents',         // Link-Hover-Farbe
            contactTextColor: 'secondaryText'  // Kontakt-Text-Farbe
        },
        fontFields: {
            headingFont: 'big',         // Kategorie-Titel
            categoryTitleFont: 'big',   // Kategorie-Titel
            linkTextFont: 'small',      // Link-Texte
            contactTextFont: 'small',   // Kontakt-Texte
            bodyFont: 'small'           // Body-Texte
        }
    },

    // ✅ GALLERY WIDGET - Automatische Font/Farb-Übertragung
    GalleryWidget: {
        colorFields: {
            backgroundColor: 'background',           // Widget-Hintergrund
            titleColor: 'primaryText',              // Titel-Farbe
            titleHighlightColor: 'accents',         // Highlight-Wörter im Titel
            descriptionColor: 'secondaryText',      // Beschreibungs-Text-Farbe
            borderColor: 'accents'                  // Border-Farbe → Space Accent Color
        },
        fontFields: {
            titleFont: 'big',                       // Titel verwendet Big Font
            titleHighlightFont: 'small',            // Highlight-Wörter verwenden Small Font (Kontrast)
            descriptionFont: 'small'                // Beschreibung verwendet Small Font
        }
    }
};

/**
 * Wendet Space-Settings auf ein Widget an (mit selektiven Updates)
 */
const applySpaceSettingsToWidget = (widget, spaceSettings, options = {}) => {
    if (!spaceSettings || !widget.type) {
        console.log('⚠️ No space settings or widget type available for styling');
        return widget;
    }
    
    const widgetType = widget.type;
    const config = WIDGET_STYLING_CONFIGS[widgetType]; // Widget-Types sind bereits in PascalCase
    
    if (!config) {
        console.log(`⚠️ No styling config found for widget type: ${widgetType}`);
        return widget;
    }
    
    const styledWidget = { ...widget };
    
    // 🎨 Farben anwenden (nur wenn explizit aktiviert oder keine Optionen angegeben)
    const shouldApplyColors = options.applyColors !== false && (options.applyColors === true || (!options.applyColors && !options.applyFonts));
    if (shouldApplyColors && config.colorFields && spaceSettings.colors && spaceSettings.colorAssignments) {
        Object.entries(config.colorFields).forEach(([widgetColorField, assignmentKey]) => {
            const colorKey = spaceSettings.colorAssignments[assignmentKey];
            const colorValue = spaceSettings.colors[colorKey];
            
            if (colorValue) {
                styledWidget[widgetColorField] = colorValue;
                console.log(`🎨 Applied ${assignmentKey} color (${colorKey}: ${colorValue}) to ${widgetColorField}`);
            }
        });
    } else if (!shouldApplyColors) {
        console.log(`🎨 [SELECTIVE] Skipping color updates for ${widgetType} (applyColors: ${options.applyColors})`);
    }
    
    // 🔤 Fonts anwenden (mit separater Big/Small Font Kontrolle)
    const shouldApplyBigFont = options.applyBigFont !== false && (options.applyBigFont === true || (options.applyFonts === true) || (!options.applyColors && !options.applyFonts && !options.applyBigFont && !options.applySmallFont));
    const shouldApplySmallFont = options.applySmallFont !== false && (options.applySmallFont === true || (options.applyFonts === true) || (!options.applyColors && !options.applyFonts && !options.applyBigFont && !options.applySmallFont));
    
    // 🎯 TEMPLATE PROTECTION: Prüfe ob Widget containerSections hat (Custom Templates)
    // Templates haben containerSections mit contentElements - diese sollen ihre eigenen Fonts behalten!
    // Wir überschreiben NICHT die containerSection Fonts, auch wenn sie leer/undefined sind
    const hasContainerSectionsWithElements = styledWidget.containerSections && 
        styledWidget.containerSections.length > 0 && 
        styledWidget.containerSections.some(container => 
            container.contentElements && container.contentElements.length > 0
        );
    
    if (hasContainerSectionsWithElements) {
        console.log(`🎨 [TEMPLATE PROTECTION] Widget has containerSections - preserving template fonts!`);
        console.log(`  ℹ️  Skipping legacy font fields (${Object.keys(config.fontFields || {}).join(', ')}) to keep containerSection fonts intact`);
    } else if (config.fontFields && spaceSettings.fonts) {
        Object.entries(config.fontFields).forEach(([widgetFontField, fontType]) => {
            if (fontType === 'big' && shouldApplyBigFont) {
                const fontValue = spaceSettings.fonts.globalBigFont;
                if (fontValue) {
                    styledWidget[widgetFontField] = fontValue;
                    console.log(`🔤 Applied BIG font (${fontValue}) to ${widgetFontField}`);
                }
            } else if (fontType === 'small' && shouldApplySmallFont) {
                const fontValue = spaceSettings.fonts.globalSmallFont;
                if (fontValue) {
                    styledWidget[widgetFontField] = fontValue;
                    console.log(`🔤 Applied SMALL font (${fontValue}) to ${widgetFontField}`);
                }
            } else if (fontType === 'big' && !shouldApplyBigFont) {
                console.log(`🔤 [SELECTIVE] Skipping BIG font update for ${widgetFontField} (applyBigFont: ${options.applyBigFont})`);
            } else if (fontType === 'small' && !shouldApplySmallFont) {
                console.log(`🔤 [SELECTIVE] Skipping SMALL font update for ${widgetFontField} (applySmallFont: ${options.applySmallFont})`);
            }
        });
    }
    
    // Globale Widget-Regeln (aus AI System) - immer anwenden
    styledWidget.marginTop = 0;
    styledWidget.marginBottom = 0;
    styledWidget.borderRadius = 0;
    
    console.log(`✅ Widget ${widgetType} styled with selective settings (colors: ${shouldApplyColors}, bigFont: ${shouldApplyBigFont}, smallFont: ${shouldApplySmallFont})`);
    return styledWidget;
};

/**
 * Haupt-Funktion: Wendet Page-Settings (inkl. Color Assignments) auf manuell hinzugefügtes Widget an
 */
export const applyPageSettingsToManualWidget = async (widget, userId, spaceId, pageName) => {
    console.log(`🎨 [WIDGET-STYLING] Applying page settings to widget: ${widget.type}`);
    
    // PRIORITÄT 1: Lade Page Settings (mit Color Assignments)
    let pageSettings = await loadPageSettings(userId, spaceId, pageName);
    
    // PRIORITÄT 2: Fallback zu Space Settings
    if (!pageSettings) {
        console.log('⚠️ [WIDGET-STYLING] No page settings found, trying space settings');
        pageSettings = await loadSpaceSettings(userId, spaceId);
    }
    
    if (!pageSettings) {
        console.log('⚠️ [WIDGET-STYLING] No settings found, using widget defaults');
        return widget;
    }
    
    // Wende Settings auf Widget an
    const styledWidget = applySpaceSettingsToWidget(widget, pageSettings);
    
    console.log(`✅ [WIDGET-STYLING] Widget styling completed for ${widget.type}`);
    return styledWidget;
};

/**
 * Legacy-Funktion für Rückwärtskompatibilität
 */
export const applySpaceSettingsToManualWidget = async (widget, userId, spaceId) => {
    console.log(`🔄 [WIDGET-STYLING] Using legacy space settings (consider upgrading to page settings)`);
    return applyPageSettingsToManualWidget(widget, userId, spaceId, null);
};

/**
 * Synchrone Version für den Fall, dass Space Settings bereits geladen sind
 * @param {Object} widget - Das Widget-Objekt
 * @param {Object} spaceSettings - Die Space-Settings
 * @param {Object} options - Optionen für selektive Updates
 * @param {boolean} options.applyColors - Ob Farben angewendet werden sollen
 * @param {boolean} options.applyFonts - Ob alle Fonts angewendet werden sollen (überschreibt applyBigFont/applySmallFont)
 * @param {boolean} options.applyBigFont - Ob nur Big Font angewendet werden soll
 * @param {boolean} options.applySmallFont - Ob nur Small Font angewendet werden soll
 */
export const applySpaceSettingsToWidgetSync = (widget, spaceSettings, options = {}) => {
    return applySpaceSettingsToWidget(widget, spaceSettings, options);
};

export default {
    applySpaceSettingsToManualWidget,
    applySpaceSettingsToWidgetSync
};
